import * as React from "react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { fetchOrders } from "@/services/getOrders";
import { useState } from "react";
import { ordersTableColumns } from "./orders-table-columns";
import { PaginatedResponse } from "@/shared/models/pagination.model";
import dayjs from "dayjs";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginationInfo } from "@/components/pagination-info";
import { OrdersFilterDialog, FilterValues } from "./orders-filter-dialog";
import { useOrderFilters } from "../hooks/useOrderFilters";

interface OrderApiResponse {
  _id: string;
  addOns: unknown[];
  service?: {
    name: string;
  };
  expressServiceName?: string;
  plateNumber: string;
  company?: {
    name: string;
  };
  totalCost: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
}

function OrdersTable() {
  const { filters, updateFilters, updateFilter, clearFilters } =
    useOrderFilters();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const orderData = useQuery<PaginatedResponse>({
    queryKey: ["orders", filters],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Convert filters to API parameters
      const apiParams = { ...filters };

      let response = await fetchOrders(apiParams);

      response = {
        ...response,
        data: response?.data?.map((order: OrderApiResponse) => ({
          id: order._id,
          addOns: order.addOns.length,
          service: order.service?.name || order?.expressServiceName,
          // expressServiceName: order.expressServiceName,
          plateNumber: order.plateNumber,
          company: order.company?.name,
          totalCost: order.totalCost,
          status: order.status,
          paymentStatus: order.paymentStatus,
          createdAt: dayjs(String(order.createdAt)).format("MM/DD/YYYY h:mm A"),
        })),
      };

      return response;
    },
  });

  // Handle filter changes from the dialog
  const handleFilterChange = (newFilters: FilterValues) => {
    // Convert Date objects to strings and reset to page 1 when filters change
    const convertedFilters = {
      ...newFilters,
      page: 1,
      startDate: newFilters.startDate?.toISOString().split("T")[0],
      endDate: newFilters.endDate?.toISOString().split("T")[0],
      updatedStart: newFilters.updatedStart?.toISOString().split("T")[0],
      updatedEnd: newFilters.updatedEnd?.toISOString().split("T")[0],
    };

    // Remove undefined values
    Object.keys(convertedFilters).forEach((key) => {
      if (
        convertedFilters[key as keyof typeof convertedFilters] === undefined
      ) {
        delete convertedFilters[key as keyof typeof convertedFilters];
      }
    });

    updateFilters(convertedFilters);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    updateFilter("page", newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: string) => {
    updateFilters({
      ...filters,
      limit: parseInt(newPageSize, 10),
      page: 1, // Reset to first page when changing page size
    });
  };

  // Update table configuration
  const table = useReactTable({
    data: Array.isArray(orderData?.data?.data) ? orderData.data.data : [],
    columns: ordersTableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    // Remove getPaginationRowModel since we're using server-side pagination
  });

  return (
    <div className="w-full">
      {orderData.isPending ? (
        [1, 2, 3, 4].map((item) => (
          <div key={item} className="flex items-center space-x-4 w-full my-8">
            <div className="space-y-2 w-full">
              <Skeleton className="h-4 w-[100%]" />
              <Skeleton className="h-4 w-[100%]" />
            </div>
          </div>
        ))
      ) : orderData.isError ? (
        <div>Error: {orderData.error.message}</div>
      ) : (
        <>
          <div className="flex items-center justify-end gap-2 mb-2">
            <OrdersFilterDialog
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
              currentFilters={{
                startDate:
                  typeof filters.startDate === "string"
                    ? new Date(filters.startDate)
                    : filters.startDate,
                endDate:
                  typeof filters.endDate === "string"
                    ? new Date(filters.endDate)
                    : filters.endDate,
                updatedStart:
                  typeof filters.updatedStart === "string"
                    ? new Date(filters.updatedStart)
                    : filters.updatedStart,
                updatedEnd:
                  typeof filters.updatedEnd === "string"
                    ? new Date(filters.updatedEnd)
                    : filters.updatedEnd,
                paymentStatus: filters.paymentStatus,
                status: filters.status,
                employee: filters.employee,
                company: filters.company,
                plateNumber: filters.plateNumber,
                service: filters.service,
                addOns: filters.addOns,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
              }}
              isLoading={orderData.isFetching}
            />
            {/* <Input
              placeholder="Filter orders..."
              value={
                (table.getColumn("service")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("service")?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            /> */}
            {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu> */}
          </div>
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={ordersTableColumns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="flex-1">
              {orderData.isSuccess && (
                <PaginationInfo
                  currentPage={orderData.data.paging.currentPage}
                  totalItems={orderData.data.paging.totalItems}
                  totalPages={orderData.data.paging.totalPages}
                  label={"orders"}
                />
              )}
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  Rows per page:
                </span>
                <Select
                  value={String(filters.limit || 10)}
                  onValueChange={handlePageSizeChange}
                  disabled={orderData.isFetching}
                >
                  <SelectTrigger className="h-8 w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1 || orderData.isFetching}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={
                    !orderData.data?.paging ||
                    (filters.page || 1) >= orderData.data.paging.totalPages ||
                    orderData.isFetching
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OrdersTable;
